import { Color } from '@material-ui/lab';
import { Locale } from 'date-fns';
import React, { useEffect, useState } from 'react';

import { AlertNotification } from '@/components/shared/components/AlertNotification';
import { NotificationSnackbar } from '@/components/shared/components/NotificationSnackbar/NotificationSnackbar';
import { SeverityLevel } from '@/components/shared/components/NotificationSnackbar/types/SeverityLevel';
import { Text } from '@/components/shared/components/Text';
import { useTranslation } from '@/components/shared/hooks/useTranslation';
import { useNotificationsContext } from '@/contexts/NotificationsContext/NotificationsContext';
import { useUserContext } from '@/contexts/UserContext/UserContext';
import { nestedProp } from '@/utils/nestedProp';
import { parseErrorMessage } from '@/utils/parseErrorMessage';

export interface NotificationSnackbarError {
  status: number;
  data: {
    error: string;
    exception?: string;
    status: string;
    schema_errors?: string;
  };
  actionMessage?: string;
}

export function NotificationManager() {
  const { getTextIdTranslation } = useTranslation();

  const { snackbarNotification: userSnackbarNotification, alertNotification, updateUserContext } =
    useUserContext();
  const {
    state: { snackbarNotification: notificationsSnackbarNotification },
    actions: { resetSnackbarNotification: resetNotificationsSnackbar },
  } = useNotificationsContext();

  const notificationSources = [
    {
      snackbarNotification: userSnackbarNotification,
      resetSnackbarNotification: () => updateUserContext({ snackbarNotification: undefined }),
    },
    {
      snackbarNotification: notificationsSnackbarNotification,
      resetSnackbarNotification: resetNotificationsSnackbar,
    },
  ];

  const initialState = {
    message: null as string | React.ReactElement | null | Locale,
    level: undefined as Color | undefined,
    resetSnackbarNotification: undefined as undefined | (() => void),
    updateContextActionName: null,
    autoHide: undefined as undefined | boolean,
    exception: undefined,
    error: undefined as NotificationSnackbarError | undefined,
  };
  const [state, setState] = useState(initialState);
  const { error: displayedError } = state;

  const getException = (action) => {
    const responseDataException =
      nestedProp(action, 'payload.response.data.exception') || nestedProp(action, 'payload.data.exception');
    const responseDataError =
      nestedProp(action, 'payload.response.data.error') || nestedProp(action, 'payload.data.error');
    const errorInfo =
      responseDataException && responseDataError ? `${responseDataException} ${responseDataError}` : 'No response';
    return ` ${action?.type}: ${errorInfo}`;
  };

  const closeNotification = (_, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    if (state.message && state.resetSnackbarNotification) {
      state.resetSnackbarNotification();
    }
    setState(initialState);
  };

  const getDefaultMessageByLevel = (level: SeverityLevel) => {
    switch (level) {
      case SeverityLevel.ERROR:
        return <Text tId={'ACTION_GENERIC_ERROR'} />;
      case SeverityLevel.INFO:
        return <Text tId={'ACTION_GENERIC_INFO'} />;
      case SeverityLevel.SUCCESS:
        return <Text tId={'ACTION_GENERIC_SUCCESS'} />;
      case SeverityLevel.WARNING:
        return <Text tId={'ACTION_GENERIC_WARNING'} />;
      default:
        return <Text tId={'DEFAULT_MESSAGE'} />;
    }
  };

  const getMessage = ({
    message,
    level,
  }: {
    message: string;
    level: SeverityLevel;
  }): string | Locale | React.JSX.Element => {
    if (displayedError?.actionMessage) {
      return getTextIdTranslation(displayedError.actionMessage)
        ? getTextIdTranslation(displayedError.actionMessage)
        : getDefaultMessageByLevel(SeverityLevel.ERROR);
    }
    return getTextIdTranslation(message) ? getTextIdTranslation(message) : getDefaultMessageByLevel(level);
  };

  const manageExceptionDialog = (notificationInContext) => {
    const errorMessage = notificationInContext.snackbarNotification.error?.message;
    const parsedErrorMessage = errorMessage && parseErrorMessage(errorMessage);
    const snackbarNotification = notificationInContext.snackbarNotification;
    const resetSnackbarNotification = notificationInContext.resetSnackbarNotification;
    const updateContextActionName = notificationInContext.updateContextActionName;
    const level = snackbarNotification && snackbarNotification.level;
    let exception;
    const autoHide = level !== SeverityLevel.ERROR;
    if (level === SeverityLevel.ERROR && parsedErrorMessage) {
      exception = getException(parsedErrorMessage.exception);
    }
    const message = getMessage({ message: notificationInContext.snackbarNotification.msg, level });
    setState((prev) => ({
      ...prev,
      message,
      error:
        level === SeverityLevel.ERROR
          ? {
              status: notificationInContext.snackbarNotification.error?.status,
              data: parsedErrorMessage,
              actionMessage: snackbarNotification.msg,
            }
          : undefined,
      exception,
      level,
      autoHide,
      resetSnackbarNotification,
      updateContextActionName,
    }));
  };

  useEffect(() => {
    const activeSource = notificationSources.find(
      (source) => source.snackbarNotification?.msg || source.snackbarNotification?.level,
    );

    if (activeSource) {
      manageExceptionDialog(activeSource);
      return;
    }

    setState((prev) => (prev.message != null ? initialState : prev));
  }, [userSnackbarNotification, notificationsSnackbarNotification]);

  const { message, autoHide, level, error } = state;
  // TODO CHECK CONSOLE WARNING findDOMNode... PRODUCED BY SNACKBAR. DEPENDENCIES MAYBE?
  return (
    <>
      <NotificationSnackbar
        open={message !== null}
        autoHide={autoHide}
        onClose={closeNotification}
        message={message}
        errorObject={error}
        level={level}
      />
      <AlertNotification tId={alertNotification?.tId} retryFn={alertNotification?.retryFn} />
    </>
  );
}
